import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Grid2,
} from '@mui/material';
import { Iconify } from '../../components/iconify';
import { useTheme } from '@mui/material/styles';
import { TablePaginationCustom } from '../../components/pagination';
import { useTable } from '../../hooks/use-table';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import PromotionBreadcrumb from './PromotionBreadcrumb';
import {
  fetchPromotionsList,
  updatePromotionStatus,
} from '../../../../../services/PromotionServices';
import { toast } from 'sonner';
import '../../../../common/commonStyles.css';

const getTypeColor = (type) => {
  switch (type) {
    case 'Buy X Get Y Free':
      return 'primary'; // Blue color
    case 'Buy X Get Y Discount':
      return 'warning';
    case 'Buy X at Discount':
      return 'info';
    case 'Multi Layer Special Offer':
      return 'secondary';
    case 'Group Offer':
      return 'error';
    case 'Percentage Discount':
      return 'info';
    case 'Fixed Amount Discount':
      return 'secondary';
    case 'Free Shipping':
      return 'success';
    case 'Buy One Get One':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Scheduled':
      return 'info';
    case 'Inactive':
      return 'error';
    default:
      return 'default';
  }
};

export default function PromotionMasterList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const table = useTable({
    defaultRowsPerPage: 10,
    defaultDense: false,
    defaultOrder: 'desc',
    defaultOrderBy: 'start_date',
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searchError, setSearchError] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [statusUpdateDialog, setStatusUpdateDialog] = useState({
    open: false,
    promotion: null,
    newStatus: '',
  });
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState({ el: null, promotion: null });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length <= 100) {
        setDebouncedSearchQuery(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    table.onChangePage(null, 0);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    loadPromotions();
  }, [table.page, table.rowsPerPage, tabValue, statusFilter, debouncedSearchQuery]);


  const loadPromotions = async () => {
    setLoading(true);
    try {
      let status;

      if (statusFilter !== 'All') {
        status = statusFilter.toLowerCase();
      } else {
        status = tabValue === 0 ? 'All' : 'inactive';
      }

      const response = await fetchPromotionsList({
        status,
        page: table.page + 1,
        pageSize: table.rowsPerPage,
        search: debouncedSearchQuery,
      });

      if (response && response.success && response.data) {
        const promotionsData = response.data.promotions || [];
        const paginationData = response.data.pagination || {};

        setPromotions(Array.isArray(promotionsData) ? promotionsData : []);
        setTotalCount(paginationData.total_count || promotionsData.length);
      } else {
        setPromotions([]);
        setTotalCount(0);
      }
    } catch (error) {
      setPromotions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setStatusFilter('All');
    table.onChangePage(null, 0);
  };

  const handleEditPromotion = (id) => {
    navigate(`/Promotions/Edit_Promotion/${id}`);
  };

  const handleViewPromotion = (id) => {
    navigate(`/Promotions/View_Promotion/${id}`);
  };

  const handleMoreOptions = (id) => {
  };

  const handleAddNewPromotion = () => {
    navigate('/Promotions/Add_Promotion');
  };

  const handleStatusUpdateClick = (promotion, newStatus) => {
    const now = new Date();
    const startDate = new Date(promotion.start_date_raw || promotion.start_date);
    const endDate = new Date(promotion.end_date_raw || promotion.end_date);

    let validationError = '';

    if (newStatus === 'scheduled') {
      if (now >= startDate) {
        validationError = `Cannot set to scheduled: Start date has already passed. Current time is past the start date.\n\nPromotion cannot be scheduled because the start date has already arrived. Use "active" instead.`;
        setStatusUpdateDialog({ open: true, promotion, newStatus });
        setStatusUpdateError(validationError);
        return;
      }
    }

    if (newStatus === 'active') {
      if (now < startDate) {
        const formattedStartDate = startDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        validationError = `Cannot activate: Start date has not arrived yet. Promotion cannot be active before its start date.\n\nStart date has not arrived yet. Use "scheduled" instead or wait until ${formattedStartDate}`;
        setStatusUpdateDialog({ open: true, promotion, newStatus });
        setStatusUpdateError(validationError);
        return;
      }

      if (now > endDate) {
        const formattedEndDate = endDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        validationError = `Cannot activate: Promotion has already expired. End date has passed.\n\nPromotion expired on ${formattedEndDate}. Cannot activate expired promotions.`;
        setStatusUpdateDialog({ open: true, promotion, newStatus });
        setStatusUpdateError(validationError);
        return;
      }
    }

    setStatusUpdateDialog({ open: true, promotion, newStatus });
    setStatusUpdateError('');
  };

  const handleStatusUpdateConfirm = async () => {
    if (!statusUpdateDialog.promotion) return;

    if (statusUpdateError) {
      handleStatusUpdateCancel();
      return;
    }

    setStatusUpdateLoading(true);
    setStatusUpdateError('');

    try {
      const response = await updatePromotionStatus(
        statusUpdateDialog.promotion.id,
        statusUpdateDialog.newStatus
      );

      if (response.success) {
        setStatusUpdateDialog({ open: false, promotion: null, newStatus: '' });
        toast.success(
          `Promotion status updated to ${statusUpdateDialog.newStatus} successfully!`
        );
        loadPromotions();
      } else {
        let errorMessage = response.message || 'Failed to update status';

        if (response.errors && Object.keys(response.errors).length > 0) {
          const errorDetails = Object.entries(response.errors)
            .map(([field, message]) => `${message}`)
            .join(', ');
          errorMessage += ': ' + errorDetails;
        }

        setStatusUpdateError(errorMessage);
      }
    } catch (error) {
      setStatusUpdateError(error.message || 'Failed to update status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleStatusUpdateCancel = () => {
    setStatusUpdateDialog({ open: false, promotion: null, newStatus: '' });
    setStatusUpdateError('');
  };

  const handleMenuOpen = (event, promotion) => {
    event.stopPropagation();
    setMenuAnchor({ el: event.currentTarget, promotion });
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchor({ el: null, promotion: null });
  };

  const handleMenuAction = (action, promotion) => {
    handleMenuClose();
    if (action === 'edit') {
      handleEditPromotion(promotion.id);
    } else if (action === 'view') {
      handleViewPromotion(promotion.id);
    } else if (action === 'nameSimilar' && promotion?.title) {
      setSearchQuery(promotion.title);
      table.onChangePage(null, 0);
    } else if (['active', 'inactive', 'scheduled'].includes(action)) {
      handleStatusUpdateClick(promotion, action);
    }
  };

  const dataInPage = Array.isArray(promotions) ? promotions : [];

  return (
    <Box>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '1.25rem', color: "#212b36" }}>
                Promotion List
              </Typography>
            </Box>

            <Grid container spacing={1} display="flex" alignItems="center" >
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Box>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    className="common-tabs"
                  >
                    <Tab label="Active Promotions" />
                    <Tab label="Inactive Promotions" />
                  </Tabs>
                </Box>
              </Grid2>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  placeholder="Search by product name, code, or promotion title..."
                  value={searchQuery}
                  fullWidth
                  className="input-border"
                  size="small"
                  error={!!searchError}
                  helperText={searchError}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length > 100) {
                      setSearchError('Search term cannot exceed 100 characters');
                      setSearchQuery(value.substring(0, 100));
                    } else {
                      setSearchQuery(value);
                      setSearchError('');
                    }
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery('');
                              setSearchError('');
                            }}
                            edge="end"
                          >
                            <Iconify icon="eva:close-fill" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
          
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: "0.875rem" }}>Status</InputLabel>
                  <Select
                    className="input-border"
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                      fontSize: "0.875rem",
                      "& .MuiSelect-select": {
                        fontSize: "0.875rem",
                      },
                    }}
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          zIndex: 9999,
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    <MenuItem value="All" sx={{ fontSize: "0.875rem" }}>All</MenuItem>
                    {tabValue === 0 && <MenuItem value="Active" sx={{ fontSize: "0.875rem" }}>Active</MenuItem>}
                    {tabValue === 0 && <MenuItem value="Scheduled" sx={{ fontSize: "0.875rem" }}>Scheduled</MenuItem>}
                    {tabValue === 1 && <MenuItem value="Inactive" sx={{ fontSize: "0.875rem" }}>Inactive</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
              <Grid2 size={{ xs: 12, md: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  className="button-variant-contained"
                  startIcon={<Iconify icon="eva:plus-fill" width={16} />}
                  onClick={handleAddNewPromotion}
                  sx={{}}
                >
                  Add New Promotion
                </Button>
              </Grid2>
            </Grid>
          </Box>
          <TableContainer
            sx={{
              width: "100%",
              overflowX: "hidden",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <Table
              size="medium"
              className="table-cell-border"
              sx={{
                tableLayout: "fixed",

              }}
            >
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell sx={{ width: "50px" }}>Code</TableCell>
                  <TableCell sx={{ width: "250px" }}>Title</TableCell>
                  <TableCell sx={{ width: "130px" }}>Type</TableCell>
                  <TableCell sx={{ width: "80px" }}>Start Date</TableCell>
                  <TableCell sx={{ width: "80px" }}>End Date</TableCell>
                  <TableCell sx={{ width: "70px" }}>Status</TableCell>
                  <TableCell sx={{ width: "190px" }}>Channel</TableCell>
                  <TableCell align="center" sx={{ width: "80px" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : dataInPage.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.5,
                        }}
                      >
                        {tabValue === 0
                          ? "'Add New Promotion' to create your first promotion."
                          : "No inactive promotions found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dataInPage.map((promotion, index) => (
                    <TableRow
                      key={promotion.id}
                      hover
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(145, 158, 171, 0.04)"
                        },
                      }}
                    >
                      <TableCell sx={{ py: 1, fontSize: "0.75rem", color: "#212b36" }}>
                        {promotion.code || promotion.promotion_code || `PROMO-${String(promotion.id || index + 1).padStart(4, '0')}`}
                      </TableCell>
                      <TableCell sx={{ py: 1, fontSize: "0.75rem", color: "#212b36" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.75rem", color: "#212b36" }}>
                          {promotion.title}
                        </Typography>
                        {debouncedSearchQuery &&
                          promotion.matched_products &&
                          promotion.matched_products.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" sx={{ color: "#637381", fontSize: "0.6875rem" }}>
                                Matched:{' '}
                                {promotion.matched_products
                                  .map((p) => `${p.code}`)
                                  .join(', ')}
                              </Typography>
                            </Box>
                          )}
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Chip
                          label={promotion.type}
                          color={getTypeColor(promotion.type)}
                          size="small"
                          variant="soft"
                          sx={{
                            fontSize: "0.6875rem",
                            height: 24,
                            maxWidth: "100%",
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1, fontSize: "0.75rem", color: "#212b36", }}>{promotion.start_date}</TableCell>
                      <TableCell sx={{ py: 1, fontSize: "0.75rem", color: "#212b36" }}>{promotion.end_date}</TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Chip
                          label={promotion.status}
                          color={getStatusColor(promotion.status)}
                          size="small"
                          variant="soft"
                          sx={{
                            fontSize: "0.6875rem",
                            height: 24,
                            maxWidth: "100%",
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1, fontSize: "0.75rem", color: "#212b36" }}>
                        <Tooltip title={promotion.channels || 'No channel specified'} arrow>
                          <Typography variant="body2" sx={{fontSize: "0.75rem", cursor: 'help', overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {promotion.channels}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(event) => handleMenuOpen(event, promotion)}
                          sx={{
                            color: "#637381",
                            "&:hover": {
                              backgroundColor: "rgba(145, 158, 171, 0.08)",
                            },
                          }}
                        >
                          <Iconify icon="eva:more-vertical-fill" width={18} height={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 2, py: 2 }}>
            <TablePaginationCustom
              page={table.page}
              dense={table.dense}
              count={totalCount}
              rowsPerPage={table.rowsPerPage}
              onPageChange={table.onChangePage}
              onRowsPerPageChange={table.onChangeRowsPerPage}
            />
          </Box>
        </Paper>

        {/* Actions Dropdown Menu */}
        <Menu
          anchorEl={menuAnchor.el}
          open={Boolean(menuAnchor.el)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            onClick: (e) => e.stopPropagation(),
            sx: {
              minWidth: 200,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          <MenuItem onClick={() => handleMenuAction('view', menuAnchor.promotion)}>
            <ListItemIcon>
              <Iconify icon="eva:eye-fill" width={20} height={20} />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleMenuAction('edit', menuAnchor.promotion)}>
            <ListItemIcon>
              <Iconify icon="solar:pen-bold" width={18} height={18} />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>

          {menuAnchor.promotion?.status === 'Active' && (
            <>
              <MenuItem onClick={() => handleMenuAction('inactive', menuAnchor.promotion)}>
                <ListItemIcon>
                  <Iconify
                    icon="eva:pause-circle-fill"
                    width={20}
                    height={20}
                    sx={{ color: theme.palette.warning.main }}
                  />
                </ListItemIcon>
                <ListItemText>Mark Inactive</ListItemText>
              </MenuItem>
              {/* <MenuItem onClick={() => handleMenuAction('scheduled', menuAnchor.promotion)}>
                <ListItemIcon>
                  <Iconify icon="eva:clock-fill" width={20} height={20} sx={{ color: theme.palette.info.main }} />
                </ListItemIcon>
                <ListItemText>Set to Scheduled</ListItemText>
              </MenuItem> */}
            </>
          )}

          {menuAnchor.promotion?.status === 'Inactive' && (
            <>
              <MenuItem onClick={() => handleMenuAction('active', menuAnchor.promotion)}>
                <ListItemIcon>
                  <Iconify
                    icon="eva:play-circle-fill"
                    width={20}
                    height={20}
                    sx={{ color: theme.palette.success.main }}
                  />
                </ListItemIcon>
                <ListItemText>Mark Active</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleMenuAction('scheduled', menuAnchor.promotion)}>
                <ListItemIcon>
                  <Iconify
                    icon="eva:clock-fill"
                    width={20}
                    height={20}
                    sx={{ color: theme.palette.info.main }}
                  />
                </ListItemIcon>
                <ListItemText>Set to Scheduled</ListItemText>
              </MenuItem>
            </>
          )}

          {menuAnchor.promotion?.status === 'Scheduled' && (
            <>
              {/* <MenuItem onClick={() => handleMenuAction('active', menuAnchor.promotion)}>
                <ListItemIcon>
                  <Iconify icon="eva:play-circle-fill" width={20} height={20} sx={{ color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText>Mark Active</ListItemText>
              </MenuItem> */}
              <MenuItem onClick={() => handleMenuAction('inactive', menuAnchor.promotion)}>
                <ListItemIcon>
                  <Iconify
                    icon="eva:pause-circle-fill"
                    width={20}
                    height={20}
                    sx={{ color: theme.palette.warning.main }}
                  />
                </ListItemIcon>
                <ListItemText>Mark Inactive</ListItemText>
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Status Update Confirmation Dialog */}
        <Dialog
          open={statusUpdateDialog.open}
          onClose={handleStatusUpdateCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {statusUpdateError ? 'Validation Error' : 'Confirm Status Update'}
          </DialogTitle>
          <DialogContent>
            {statusUpdateError ? (
              <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                {statusUpdateError}
              </Alert>
            ) : (
              <>
                <Typography variant="body1">
                  Are you sure you want to change the status of promotion{' '}
                  <strong>"{statusUpdateDialog.promotion?.title}"</strong> to{' '}
                  <strong>{statusUpdateDialog.newStatus}</strong>?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This action will immediately update the promotion status and may affect
                  its visibility and functionality.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {statusUpdateError ? (
              <Button onClick={handleStatusUpdateCancel} variant="contained">
                OK
              </Button>
            ) : (
              <>
                <Button
                  className="button-variant-outlined"
                  variant="outlined"
                  onClick={handleStatusUpdateCancel} disabled={statusUpdateLoading}>
                  Cancel
                </Button>
                <Button
                  onClick={handleStatusUpdateConfirm}
                  variant="contained"
                  className="button-variant-contained"
                  disabled={statusUpdateLoading}
                  startIcon={statusUpdateLoading ? <CircularProgress size={16} /> : null}
                >
                  {statusUpdateLoading ? 'Updating...' : 'Confirm'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
